// coverage.tsx — their blueprint, one page at a time, with one card beside it.
//
// WHY (Erez, 17 Aug 2026):
//   "Next to each PDF page put one card that is in the same shape and size of
//    the PDF page and put it next to it, and in that card just write a short
//    summary about this page — what have we learned, what have we updated in the
//    BRD, and what questions come of it. But in a very tight summary. Do that
//    for all the pages."
//
// ── THIS IS THE THIRD SHAPE, AND THE FIRST TWO FAILED THE SAME WAY ──────────
// v1 was one row per page with a list of tagged items. v2 broke that into one
// arrow per SECTION with denser boxes, and then made the page still 3.5× bigger
// so the boxes would fit beside it. Every version was MORE correct and LESS
// usable, and he said so plainly: *"I'm overloaded with information… why is this
// process so difficult?"*
//
// **The diagnosis was not the layout. §1 alone produced 57 items and 15
// questions, so the document will produce ~350. No arrangement makes 350 items
// feel light.** The mistake was showing a LIST where a reader needs a VERDICT:
// a coverage map's job is to tell you that you can STOP reading, and the first
// two versions told you to keep reading.
//
// So the card answers three questions in prose and nothing else:
//   WHAT WE LEARNED · WHAT WE UPDATED IN THE BRD · WHAT QUESTIONS CAME OF IT
//
// The full extraction still exists — `doc_extracts.items` holds every tagged
// item, and the ten BRD pages hold the detail. It is simply not on this surface.
// **Nothing was deleted to make this readable; it was moved behind the summary.**
//
// ── WHY IT IS A TABLE AND NOT THIS FILE ─────────────────────────────────────
// `doc_extracts` (sql/053, reshaped to one row per page in sql/054) through
// /api/doc-extracts. Walking a page and updating a row redraws this with no
// deploy. Hardcoding the map here would guarantee it drifts from the documents
// it summarises — the exact defect it exists to make visible.
//
// ⚠ ALL 24 PAGES HAVE A CARD, including the 22 we have not read. A coverage map
// that lists only what you have done cannot tell you what is left.

import { useCallback, useEffect, useState } from "react";
import { useApi } from "@/lib/api";
import { blocksForDoc, norm, type Block, type Highlight } from "../PdfMarkupFrame";

const FONT = "'Inter Tight', Inter, system-ui, -apple-system, sans-serif";
const MONO = "ui-monospace, SFMono-Regular, Menlo, monospace";

/** The 24 page stills, already on R2 for <PresentationFrame>. */
const SLIDES = "https://pub-f9d4dc80715b4656bb344d288227078e.r2.dev/pitch-doc/consultancy/outform/blueprint/slides";
/** The PDF itself — read for TEXT GEOMETRY ONLY, never rasterised here. */
const PDF = "https://pub-f9d4dc80715b4656bb344d288227078e.r2.dev/pitch-doc/consultancy/outform/blueprint/outform-ai-estimating-blueprint-rev10.pdf";

const MARKER = "rgba(250, 214, 62, 0.45)";

/** US Letter. The card is drawn to the SAME box as the page, which is the whole
 *  point of the layout: a page and its summary are one pair, the same size, and
 *  a card that has run out of room is telling you the summary is too long. */
const PAGE_W = 860;
const PAGE_ASPECT = 1275 / 1650;
const PAGE_H = PAGE_W / PAGE_ASPECT;

/** ── HOW MANY PAIRS FIT ON A ROW, AND WHY IT IS NOT TEN ─────────────────────
 *  Erez asked for 10 · 10 · 4. The arithmetic says no, and the reason is the
 *  bug that ate four sheets off this board yesterday morning:
 *
 *    Chrome will not paint a composited layer past ~16,384px, and
 *    `.react-flow__viewport` IS that layer, scaled by the board zoom. So the
 *    usable board width is 16,384 / zoom — 6,554px at MAX_ZOOM 2.5.
 *
 *  A pair is a page and its card side by side: 860 + 34 + 860 = 1,754px.
 *
 *      3 pairs =  5,374px  → survives zoom 3.05   ← fits at any zoom
 *      4 pairs =  7,184px  → breaks past 2.28
 *      5 pairs =  8,994px  → breaks past 1.82
 *     10 pairs = 18,044px  → breaks past 0.91
 *
 *  At ten across, the moment he zooms in to READ a card — which is the entire
 *  point of the section — everything right of the third pair disappears. And it
 *  is not local: the layer spans the whole board, so a 18,000px-wide section
 *  caps the max usable zoom for every OTHER section too.
 *
 *  Three it is, at the page size he chose. If he wants more across, the page has
 *  to shrink — 700px gives 4, 600px gives 5, 500px gives 6 — and 500px was
 *  already rejected as unreadable. That trade is his to make, not mine to
 *  assume, so it is written down rather than quietly taken. */
const PER_ROW = 3;
const ROW_W = PER_ROW * (PAGE_W * 2 + 34) + (PER_ROW - 1) * 56;

type Row = {
  id: string; page: number; section: string; title: string;
  status: "not_read" | "walking" | "walked";
  learned: string | null; updated: string | null; questions: string | null;
};

// ── THE UNREAD PAGES ARE NO LONGER DIMMED (Erez, 18 Aug 2026) ───────────────
// Until now an unwalked page and its card were drawn at 0.5 / 0.55 opacity, so
// two thirds of this section sat under a grey wash. His words: "there's still
// like a gray over all the files that I didn't read — can you remove this gray?
// We now have this yellow marker."
//
// The dimming was doing a job the marker now does better, and doing it worse:
// it answered "have we walked this page" for the WHOLE page, at a moment when
// nothing had been walked, so it made the map hard to READ in order to report
// progress nobody needed reporting that way. The yellow marks say what has
// actually been settled, paragraph by paragraph, and they are on the page
// itself. Two signals for one question, and the one that greyed out the source
// document was the weaker of them.
//
// The status DOT and its label stay: a one-word verdict in the corner of a card
// costs nothing and still answers "where is the walk". What it must not do is
// make the document harder to look at.
const STATUS: Record<Row["status"], { dot: string; label: string; fg: string }> = {
  walked:   { dot: "#4F8A5B", label: "walked",    fg: "#3C6B48" },
  walking:  { dot: "#D9A441", label: "in flight", fg: "#8A5A1B" },
  not_read: { dot: "#C9C6BE", label: "not read",  fg: "#8C948E" },
};

/** One block of the card: a label and its points.
 *
 *  POINTS, NOT PROSE (Erez, 18 Aug): "it needs to be points so I can consume it
 *  at high level — all the detail is in the PDF anyway." That last clause is the
 *  licence: the source document is RIGHT THERE, two feet to the left, so this
 *  card never has to carry the argument. It only has to say what changed.
 *
 *  So the field is newline-separated and each line is one point. No line wraps
 *  to more than about two lines at this width, and if it does, it is a paragraph
 *  wearing a bullet and belongs in one of the ten documents instead. */
function Block({ label, body, accent }: { label: string; body: string; accent: string }) {
  const points = body.split("\n").map((t) => t.trim()).filter(Boolean);
  return (
    <div>
      <div style={{
        fontFamily: MONO, fontSize: 10, letterSpacing: "0.16em", fontWeight: 700,
        color: accent, marginBottom: 9,
      }}>{label}</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
        {points.map((t, i) => (
          <div key={i} style={{ display: "flex", gap: 9, alignItems: "baseline" }}>
            <span style={{ color: accent, fontSize: 13, lineHeight: 1.45, flexShrink: 0 }}>&bull;</span>
            <span style={{ fontSize: 15, lineHeight: 1.45, color: "#2A302C" }}>{t}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function OutformCoverage({ doc = "outform/blueprint-rev10" }: { doc?: string }) {
  const api = useApi();
  const [rows, setRows] = useState<Row[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** Click-to-mark, folded in from the retired <PdfMarkupFrame> (Erez, 18 Aug):
   *  "remove this tool… although it's a really cool tool, just add the
   *   functionality of it to their blueprint, what we took from each part."
   *  He is right that two surfaces showing the same 24 pages was one too many —
   *  and the marks belong ON the page you are reading the summary against. */
  const [blocks, setBlocks] = useState<Record<number, Block[]>>({});
  const [marks, setMarks] = useState<Highlight[]>([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api(`/api/doc-extracts?doc=${encodeURIComponent(doc)}`);
        if (!res.ok) { if (!cancelled) setError(`${res.status} — could not load the coverage map`); return; }
        const body = (await res.json()) as Row[];
        if (!cancelled) setRows(Array.isArray(body) ? body : []);
      } catch (e) {
        if (!cancelled) setError(String(e));
      }
    })();
    return () => { cancelled = true; };
    // `api` is a fresh closure every render — including it loops forever.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc]);

  // Text geometry for every page. No canvas: the stills carry the picture.
  useEffect(() => {
    let cancelled = false;
    blocksForDoc(PDF)
      .then((b) => { if (!cancelled) setBlocks(b); })
      .catch(() => { /* the marker is additive — a failed load must not take the map down */ });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api(`/api/doc-highlights?doc=${encodeURIComponent(doc)}`);
        if (!res.ok) return;
        const rowsOut = (await res.json()) as Highlight[];
        if (!cancelled) setMarks(Array.isArray(rowsOut) ? rowsOut : []);
      } catch { /* same: additive */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc]);

  /** One gesture: click a paragraph to mark it, click again to clear it. The
   *  mark is a row, not browser state, so the agent walking the document can
   *  read what a human has actually settled. Optimistic, and it rolls back
   *  loudly rather than silently if the write fails. */
  const toggle = useCallback(async (b: Block) => {
    const existing = marks.find((h) => h.page === b.page && norm(h.quote ?? "") === b.text);
    if (existing) {
      setMarks((m) => m.filter((h) => h.id !== existing.id));
      try {
        const res = await api(`/api/doc-highlights?id=${existing.id}`, { method: "DELETE" });
        if (!res.ok) throw new Error(String(res.status));
      } catch (e) {
        setMarks((m) => [...m, existing]);
        setError(`could not clear that mark — ${e instanceof Error ? e.message : "failed"}`);
      }
      return;
    }
    const optimistic: Highlight = {
      id: `tmp-${b.page}-${b.text.slice(0, 24)}`, page: b.page, rects: b.rects, quote: b.text, color: "yellow",
    };
    setMarks((m) => [...m, optimistic]);
    try {
      const res = await api("/api/doc-highlights", {
        method: "POST",
        body: JSON.stringify({ doc_slug: doc, page: b.page, rects: b.rects, quote: b.text, color: "yellow", created_by: "erez" }),
      });
      if (!res.ok) throw new Error(String(res.status));
      const saved = (await res.json()) as Highlight;
      setMarks((m) => m.map((h) => (h.id === optimistic.id ? saved : h)));
    } catch (e) {
      setMarks((m) => m.filter((h) => h.id !== optimistic.id));
      setError(`could not save that mark — ${e instanceof Error ? e.message : "failed"}`);
    }
  }, [api, doc, marks]);

  if (error) return <div style={{ fontFamily: MONO, fontSize: 13, color: "#C0492F" }}>{error}</div>;
  if (!rows) return <div style={{ fontFamily: MONO, fontSize: 13, color: "#8C948E" }}>loading…</div>;

  const done = rows.filter((r) => r.status === "walked").length;

  return (
    <div style={{ fontFamily: FONT, display: "flex", flexDirection: "column", gap: 34, width: ROW_W }}>
      {/* The count first. This surface exists to answer "are we through it yet",
          and a number answers that faster than twenty-four cards do. */}
      <div style={{ display: "flex", alignItems: "baseline", gap: 18, paddingInline: 4 }}>
        <div style={{ fontFamily: MONO, fontSize: 12, letterSpacing: "0.14em", textTransform: "uppercase", color: "#8C948E" }}>
          {done} of {rows.length} pages walked
        </div>
        <div style={{ flex: 1, height: 4, background: "#DDD9D2", borderRadius: 2, overflow: "hidden", maxWidth: 420 }}>
          <div style={{ width: `${(done / rows.length) * 100}%`, height: "100%", background: "#4F8A5B" }} />
        </div>
      </div>

      {/* Pairs wrap across ROW_W — three to a row. Each pair is one page and its
          card, kept side by side and never split across a row break. */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 56, alignItems: "flex-start" }}>
      {rows.map((r) => {
        const st = STATUS[r.status] ?? STATUS.not_read;
        // Still used for the card's CONTENT — an unwalked page says "not read
        // yet" instead of showing three empty blocks. It no longer dims anything.
        const read = r.status !== "not_read";
        return (
          <div key={r.id} style={{ display: "flex", gap: 34, alignItems: "flex-start", flexShrink: 0 }}>
            {/* ── the page ────────────────────────────────────────────────── */}
            {/* The page, and the marker over it. `nowheel nodrag nopan` +
                stopPropagation so clicking a paragraph never pans the board —
                the three React-Flow traps this used to cost us. */}
            <div
              className="nowheel nodrag nopan"
              onPointerDown={(e) => e.stopPropagation()}
              style={{
                position: "relative", width: PAGE_W, height: PAGE_H, flexShrink: 0,
                pointerEvents: "auto",
              }}
            >
              <img
                // slide-01.jpg … slide-24.jpg — the naming <PresentationFrame>
                // already uses against this same R2 prefix. Static ?v= tag, never
                // Date.now(): a timestamp re-downloads every still on every open.
                src={`${SLIDES}/slide-${String(r.page).padStart(2, "0")}.jpg?v=1`}
                alt={`page ${r.page}`}
                decoding="async"
                style={{
                  width: PAGE_W, height: PAGE_H, objectFit: "contain", objectPosition: "top",
                  display: "block", background: "#FFFFFF", border: "1px solid #E3DED4", borderRadius: 4,
                }}
              />

              {/* ONE GESTURE: click a paragraph to mark it, click again to clear.
                  Geometry is PERCENT, computed from the PDF's own text content at
                  scale 1 — so it lands correctly at any width and any board zoom.
                  A rect in pixels is correct once, on the screen that made it. */}
              {(blocks[r.page] ?? []).map((b, bi) => {
                const on = marks.some((h) => h.page === b.page && norm(h.quote ?? "") === b.text);
                return (
                  <div key={bi}>
                    {on &&
                      b.rects.map((rc, ri) => (
                        <div
                          key={ri}
                          style={{
                            position: "absolute", pointerEvents: "none",
                            left: `${rc.x}%`, top: `${rc.y}%`, width: `${rc.w}%`, height: `${rc.h}%`,
                            background: MARKER, mixBlendMode: "multiply",
                          }}
                        />
                      ))}
                    <div
                      onClick={(e) => { e.stopPropagation(); void toggle(b); }}
                      title={b.text.slice(0, 140)}
                      style={{
                        position: "absolute", cursor: "pointer",
                        left: `${b.hit.x}%`, top: `${b.hit.y}%`, width: `${b.hit.w}%`, height: `${b.hit.h}%`,
                        background: "transparent",
                      }}
                      onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.background = "rgba(47,62,79,.07)"; }}
                      onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.background = "transparent"; }}
                    />
                  </div>
                );
              })}
            </div>

            {/* ── the card: SAME BOX as the page ──────────────────────────── */}
            <div style={{
              width: PAGE_W, height: PAGE_H, flexShrink: 0, boxSizing: "border-box",
              background: "#FFFFFF", border: "1px solid #E3DED4", borderRadius: 4,
              padding: "38px 44px", display: "flex", flexDirection: "column", gap: 26,
            }}>
              <div>
                <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 8 }}>
                  <span style={{ fontFamily: MONO, fontSize: 13, letterSpacing: "0.12em", color: "#8C948E" }}>
                    PAGE {r.page}
                  </span>
                  <span style={{ fontFamily: MONO, fontSize: 13, fontWeight: 700, color: "#2F3E4F" }}>{r.section}</span>
                  <span style={{ flex: 1 }} />
                  <span style={{ width: 7, height: 7, borderRadius: 999, background: st.dot }} />
                  <span style={{ fontFamily: MONO, fontSize: 10, letterSpacing: "0.1em", textTransform: "uppercase", color: st.fg }}>
                    {st.label}
                  </span>
                </div>
                <div style={{ fontSize: 21, fontWeight: 600, lineHeight: 1.25, color: "#1B211E" }}>{r.title}</div>
              </div>

              {/* An unwalked page says so in one line and stops. Empty is the
                  honest state, and a placeholder that pretends otherwise is the
                  thing that makes a map untrustworthy. */}
              {!read ? (
                <div style={{ fontFamily: MONO, fontSize: 13, color: "#A9A79F", marginTop: 6 }}>
                  not read yet
                </div>
              ) : (
                <>
                  {r.learned && <Block label="WHAT WE LEARNED" body={r.learned} accent="#8A5A1B" />}
                  {r.updated && <Block label="WHAT WE UPDATED IN THE BRD" body={r.updated} accent="#3A536F" />}
                  {r.questions && <Block label="QUESTIONS THIS RAISED" body={r.questions} accent="#7A3B49" />}
                </>
              )}
            </div>
          </div>
        );
      })}
      </div>
    </div>
  );
}
